import type {
  InsuranceProductPolicyItemConfiguration,
  InsuranceProductPolicyItemConfigurationResolver,
} from '../../../model/policy-item-configuration.ts'
import type { PolicyItems } from '../../../model/policy-item.ts'
import type { FrAupaPolicyItem, FrAupaPolicyItemsState } from '../fr-aupa-insurance.ts'

import { getFrAupaPolicyItemCover_v1_0_0 } from './cover-by-policy-item.ts'
import { frAupaDeductibleByPolicyItem_v1_0_0 as deductibles } from './deductible-by-policy-item.ts'
import { frAupaLimitOfIndemnityByPolicyItem_v1_0_0 as limits } from './limit-of-indemnity-by-policy-item.ts'

const unavailable: InsuranceProductPolicyItemConfiguration = {
  isHidden: true,
  canBeUpdated: false,
  canBeSelected: false,
}

function editConfigurationOf(
  policyItemName: FrAupaPolicyItem,
  { hasLimitSelector = false } = {},
): InsuranceProductPolicyItemConfigurationResolver<FrAupaPolicyItemsState> {
  const coverByFormula = getFrAupaPolicyItemCover_v1_0_0(policyItemName)

  return ({ state }) => {
    const cover = state.frAupaFormula ? coverByFormula[state.frAupaFormula] : undefined
    if (!cover) return unavailable
    return { isHidden: false, canBeUpdated: hasLimitSelector, canBeSelected: cover === 'optional' }
  }
}

/**
 * The formula offers enhanced compensation at all-risks, but only a young enough vehicle without a financial-loss
 * cover already held may have it: the trigger decides, and excludes it otherwise.
 */
function enhancedCompensationEditConfiguration(): InsuranceProductPolicyItemConfigurationResolver<FrAupaPolicyItemsState> {
  const byFormula = editConfigurationOf('enhancedCompensation')

  return (args) =>
    args.state.frAupaEnhancedCompensationPolicyItem?.optionality.type === 'excluded' ? unavailable : byFormula(args)
}

/**
 * Shown when the formula carries the cover, never selectable: the reduced deductible follows the general deductible
 * the customer picks on the quote page, so a checkbox here would be a second place to answer the same question.
 */
function displayedWithoutCheckboxOf(
  policyItemName: FrAupaPolicyItem,
): InsuranceProductPolicyItemConfigurationResolver<FrAupaPolicyItemsState> {
  const coverByFormula = getFrAupaPolicyItemCover_v1_0_0(policyItemName)

  return ({ state }) => {
    const cover = state.frAupaFormula ? coverByFormula[state.frAupaFormula] : undefined
    return cover ? { isHidden: false, canBeUpdated: false, canBeSelected: false } : unavailable
  }
}

/**
 * The 19 AXA codes, one policy item each. The `optionality` list is every state the cover table can produce for the
 * item across the three formulas; the value a given subscription carries is written by the trigger. `dependsOn`
 * transcribes the Dependency column of the GUARANTEES tab, except that the reduced deductible names fire and theft
 * only: it is sold at third-party-plus, where all-accident damage does not exist.
 */
export const frAupaPolicyItems_v1_0_0 = {
  civilLiability: {
    name: 'civilLiability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.civilLiability.limit,
    possibleDeductibles: deductibles.civilLiability.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('civilLiability'),
    additionalInfo: {
      limitOfIndemnity: limits.civilLiability.additionalInfo,
      deductible: deductibles.civilLiability.additionalInfo,
    },
  },
  operatingLiability: {
    name: 'operatingLiability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.operatingLiability.limit,
    possibleDeductibles: deductibles.operatingLiability.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('operatingLiability'),
    additionalInfo: {
      limitOfIndemnity: limits.operatingLiability.additionalInfo,
      deductible: deductibles.operatingLiability.additionalInfo,
    },
  },
  ecologicalDamage: {
    name: 'ecologicalDamage',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.ecologicalDamage.limit,
    possibleDeductibles: deductibles.ecologicalDamage.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('ecologicalDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.ecologicalDamage.additionalInfo,
      deductible: deductibles.ecologicalDamage.additionalInfo,
    },
  },
  criminalDefenseAndRecourse: {
    name: 'criminalDefenseAndRecourse',
    type: 'service',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.criminalDefenseAndRecourse.limit,
    possibleDeductibles: deductibles.criminalDefenseAndRecourse.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('criminalDefenseAndRecourse'),
    additionalInfo: {
      limitOfIndemnity: limits.criminalDefenseAndRecourse.additionalInfo,
      deductible: deductibles.criminalDefenseAndRecourse.additionalInfo,
    },
  },
  driverProtection: {
    name: 'driverProtection',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.driverProtection.limit,
    possibleDeductibles: deductibles.driverProtection.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('driverProtection'),
    additionalInfo: {
      limitOfIndemnity: limits.driverProtection.additionalInfo,
      deductible: deductibles.driverProtection.additionalInfo,
    },
  },
  glass: {
    name: 'glass',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.glass.limit,
    possibleDeductibles: deductibles.glass.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('glass'),
    additionalInfo: {
      limitOfIndemnity: limits.glass.additionalInfo,
      deductible: deductibles.glass.additionalInfo,
    },
  },
  naturalDisasters: {
    name: 'naturalDisasters',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.naturalDisasters.limit,
    possibleDeductibles: deductibles.naturalDisasters.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('naturalDisasters'),
    additionalInfo: {
      limitOfIndemnity: limits.naturalDisasters.additionalInfo,
      deductible: deductibles.naturalDisasters.additionalInfo,
    },
  },
  technologicalDisasters: {
    name: 'technologicalDisasters',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.technologicalDisasters.limit,
    possibleDeductibles: deductibles.technologicalDisasters.deductible,
    dependsOn: ['naturalDisasters'],
    editConfiguration: editConfigurationOf('technologicalDisasters'),
    additionalInfo: {
      limitOfIndemnity: limits.technologicalDisasters.additionalInfo,
      deductible: deductibles.technologicalDisasters.additionalInfo,
    },
  },
  climateEvents: {
    name: 'climateEvents',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.climateEvents.limit,
    selectDeductible: deductibles.climateEvents.select,
    dependsOn: [],
    editConfiguration: editConfigurationOf('climateEvents'),
    additionalInfo: {
      limitOfIndemnity: limits.climateEvents.additionalInfo,
      deductible: deductibles.climateEvents.additionalInfo,
    },
  },
  terrorism: {
    name: 'terrorism',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.terrorism.limit,
    selectDeductible: deductibles.terrorism.select,
    dependsOn: [],
    editConfiguration: editConfigurationOf('terrorism'),
    additionalInfo: {
      limitOfIndemnity: limits.terrorism.additionalInfo,
      deductible: deductibles.terrorism.additionalInfo,
    },
  },
  fire: {
    name: 'fire',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.fire.limit,
    selectDeductible: deductibles.fire.select,
    dependsOn: [],
    editConfiguration: editConfigurationOf('fire'),
    additionalInfo: {
      limitOfIndemnity: limits.fire.additionalInfo,
      deductible: deductibles.fire.additionalInfo,
    },
  },
  theft: {
    name: 'theft',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.theft.limit,
    selectDeductible: deductibles.theft.select,
    dependsOn: [],
    editConfiguration: editConfigurationOf('theft'),
    additionalInfo: {
      limitOfIndemnity: limits.theft.additionalInfo,
      deductible: deductibles.theft.additionalInfo,
    },
  },
  allAccidentDamage: {
    name: 'allAccidentDamage',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.allAccidentDamage.limit,
    selectDeductible: deductibles.allAccidentDamage.select,
    dependsOn: [],
    editConfiguration: editConfigurationOf('allAccidentDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.allAccidentDamage.additionalInfo,
      deductible: deductibles.allAccidentDamage.additionalInfo,
    },
  },
  enhancedCompensation: {
    name: 'enhancedCompensation',
    type: 'improvement',
    optionality: ['optional', 'excluded'],
    possibleLimitOfIndemnities: limits.enhancedCompensation.limit,
    possibleDeductibles: deductibles.enhancedCompensation.deductible,
    dependsOn: ['allAccidentDamage'],
    editConfiguration: enhancedCompensationEditConfiguration(),
    additionalInfo: {
      limitOfIndemnity: limits.enhancedCompensation.additionalInfo,
      deductible: deductibles.enhancedCompensation.additionalInfo,
    },
  },
  extendedDriver: {
    name: 'extendedDriver',
    type: 'improvement',
    optionality: ['optional'],
    possibleLimitOfIndemnities: limits.extendedDriver.limit,
    possibleDeductibles: deductibles.extendedDriver.deductible,
    dependsOn: ['driverProtection'],
    editConfiguration: editConfigurationOf('extendedDriver', { hasLimitSelector: true }),
    additionalInfo: {
      limitOfIndemnity: limits.extendedDriver.additionalInfo,
      deductible: deductibles.extendedDriver.additionalInfo,
    },
  },
  vehicleEquipment: {
    name: 'vehicleEquipment',
    type: 'guarantee',
    optionality: ['optional', 'excluded'],
    possibleLimitOfIndemnities: limits.vehicleEquipment.limit,
    possibleDeductibles: deductibles.vehicleEquipment.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('vehicleEquipment'),
    additionalInfo: {
      limitOfIndemnity: limits.vehicleEquipment.additionalInfo,
      deductible: deductibles.vehicleEquipment.additionalInfo,
    },
  },
  vehicleContents: {
    name: 'vehicleContents',
    type: 'guarantee',
    optionality: ['optional', 'excluded'],
    possibleLimitOfIndemnities: limits.vehicleContents.limit,
    possibleDeductibles: deductibles.vehicleContents.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('vehicleContents'),
    additionalInfo: {
      limitOfIndemnity: limits.vehicleContents.additionalInfo,
      deductible: deductibles.vehicleContents.additionalInfo,
    },
  },
  reducedDeductible: {
    name: 'reducedDeductible',
    type: 'improvement',
    optionality: ['optional', 'excluded'],
    possibleLimitOfIndemnities: limits.reducedDeductible.limit,
    possibleDeductibles: deductibles.reducedDeductible.deductible,
    dependsOn: ['fire', 'theft'],
    editConfiguration: displayedWithoutCheckboxOf('reducedDeductible'),
    additionalInfo: {
      limitOfIndemnity: limits.reducedDeductible.additionalInfo,
      deductible: deductibles.reducedDeductible.additionalInfo,
    },
  },
  assistance: {
    name: 'assistance',
    type: 'service',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.assistance.limit,
    possibleDeductibles: deductibles.assistance.deductible,
    dependsOn: [],
    editConfiguration: editConfigurationOf('assistance'),
    additionalInfo: {
      limitOfIndemnity: limits.assistance.additionalInfo,
      deductible: deductibles.assistance.additionalInfo,
    },
  },
} as const satisfies PolicyItems<FrAupaPolicyItem, FrAupaPolicyItemsState>
